/* Service-worker registration and the update signal.

   Registration is deliberately not automatic (`injectRegister: null` in the
   Vite config) so this module owns when it happens: after the first paint, so
   the worker's install does not compete with the route chunk for the same
   connection on a first visit.

   ── Why this is not simply "always prompt" ──────────────────────────────────

   It used to be. A new worker installed, went to `waiting`, and sat there
   until the user pressed a button in UpdatePrompt. That is the correct
   behaviour for a user standing in a checkout — swapping the running app's
   chunks underneath them breaks the next lazy route they navigate to — but it
   was applied to everyone, including the far commoner case of somebody who
   had just opened the app and touched nothing yet. Miss the prompt once and
   the old worker keeps answering every navigation from its own precache, so
   the site stays on the old build indefinitely. No amount of cache headers
   fixes that: the worker intercepts before the network, so the server never
   gets asked.

   The rule now is that an update applies itself whenever nobody is watching
   or nobody has started, and asks only when applying would interrupt
   something:

     · tab hidden          → swap now, the reload is never seen
     · page just opened    → swap now, there is no session to lose
     · a paying route      → always ask; see SENSITIVE below
     · anything else       → ask, as before

   ── Why it also polls ───────────────────────────────────────────────────────

   `registerSW` checks for a new worker once, at registration. A tab left open
   across a deploy — a phone with the app in the background for a day — never
   checks again and never learns. So the registration is also re-checked on an
   interval and whenever the tab comes back to the foreground. */

import { registerSW } from 'virtual:pwa-register'

type Listener = (available: boolean) => void

const listeners = new Set<Listener>()
let updateAvailable = false
let applyUpdate: ((reload?: boolean) => Promise<void>) | null = null

/* Routes where a reload would cost the user something they cannot get back:
   a part-filled checkout, a basket, a live order, a half-typed address. On
   these the update always waits for an explicit press. */
const SENSITIVE = /^\/(basket|checkout|auth|order\/)/

/* How long after load a visit still counts as "not started". Long enough to
   cover a slow worker install on a cold cache, short enough that it cannot
   catch someone who has begun reading. */
const GRACE_MS = 6000

/* A background tab is worth re-checking about this often. Hourly is well
   inside the 24h browsers already cap a worker script's freshness at, and it
   costs one conditional request. */
const POLL_MS = 60 * 60 * 1000

export function onUpdateAvailable(fn: Listener): () => void {
  listeners.add(fn)
  // Late subscribers still learn about an update that landed before they
  // mounted — the prompt component mounts after registration on a fast cache.
  if (updateAvailable) fn(true)
  return () => listeners.delete(fn)
}

/** Tells the waiting worker to activate, then reloads onto it. */
export async function applyPendingUpdate(): Promise<void> {
  await applyUpdate?.(true)
}

export function registerServiceWorker(): void {
  if (import.meta.env.DEV) return
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return

  const startedAt = Date.now()

  applyUpdate = registerSW({
    immediate: true,

    onRegisteredSW(_swUrl, registration) {
      if (!registration) return

      const check = () => {
        // An offline check is a guaranteed rejection; skip it rather than
        // log one failed update poll per hour for a user on a train.
        if (navigator.onLine === false) return
        void registration.update().catch(() => {
          /* A failed poll is not an error worth surfacing — the next one, or
             the next page load, tries again. */
        })
      }

      const timer = setInterval(check, POLL_MS)
      // A phone unlocking onto a backgrounded tab is the single most likely
      // moment for a deploy to have happened since the tab last looked.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check()
      })
      window.addEventListener('pagehide', () => clearInterval(timer))
    },

    onNeedRefresh() {
      updateAvailable = true

      /* Nobody is looking, or nobody has started. Take it now — this is the
         case that used to strand people on an old build. */
      const unattended = document.visibilityState === 'hidden'
      const barelyStarted = Date.now() - startedAt < GRACE_MS
      const safeRoute = !SENSITIVE.test(window.location.pathname)

      if (safeRoute && (unattended || barelyStarted)) {
        void applyUpdate?.(true)
        return
      }

      for (const fn of listeners) fn(true)
    },

    onOfflineReady() {
      // Intentionally quiet. "Ready to work offline" is a fact about the app,
      // not an event in the user's task, and a toast for it interrupts
      // whatever they actually opened the app to do.
    },

    onRegisterError(error: unknown) {
      console.error('[hexclean] service worker registration failed', error)
    },
  })
}
